import { Shield, Clock, ChevronRight, HelpCircle, Info, Building2, UserCircle, LogOut, Award, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import API_BASE_URL from '../../config';
import HelpScreen from '../dashboard/HelpScreen';
import LeaderboardView from '../../components/LeaderboardView';

const SettingsTab = () => {
  const { logout } = useContext(AuthContext);
  const [gymData, setGymData] = useState(null);
  const [loading, setLoading] = useState(true);
  const userInfo = JSON.parse(localStorage.getItem('fitscan_user'));
  const [activeView, setActiveView] = useState(null); // 'gym', 'owner', 'help', 'about', 'agreements', 'freeze'
  const [freezeSettings, setFreezeSettings] = useState(null);
  const [loadingFreeze, setLoadingFreeze] = useState(false);
  const [savingFreeze, setSavingFreeze] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [freezeConflictCount, setFreezeConflictCount] = useState(0);
  const [consents, setConsents] = useState([]);
  const [loadingConsents, setLoadingConsents] = useState(false);
  const [updatingConsentId, setUpdatingConsentId] = useState(null);
  const [banner, setBanner] = useState({
    type: null,
    message: '',
    timeoutId: null
  });

  useEffect(() => {
    fetchGymDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeView === 'agreements') {
      fetchConsents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);

  useEffect(() => {
    if (activeView === 'freeze') {
      fetchFreezeSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView]);


  const showBanner = (type, message) => {
    // Clear existing timeout if it exists
    if (banner.timeoutId) {
      clearTimeout(banner.timeoutId);
    }

    // Set new banner immediately
    setBanner({ type, message, timeoutId: null });

    // Create timeout with useRef to avoid stale closure
    const timeoutId = setTimeout(() => {
      setBanner({ type: null, message: '', timeoutId: null });
    }, 4000);

    // Store timeoutId in state
    setBanner(prev => ({ ...prev, timeoutId }));
  };


  // Cleanup on unmount
  // Cleanup useEffect - FIXED
  useEffect(() => {
    return () => {
      if (banner?.timeoutId) {
        clearTimeout(banner.timeoutId);
      }
    };
  }, [banner?.timeoutId]); // ✅ Add dependency




  const fetchGymDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/gym/gymdetails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
        },
        body: JSON.stringify(userInfo?.gymcode || ''),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setGymData(data);
    } catch (error) {
      console.error('Failed to load:', error);
      // Fallback dummy data for testing
      setGymData({
        name: "FitScan Elite Gym",
        phonenumber: "9876543210",
        ownerName: "John Doe",
        ownerEmail: "owner@fitscan.com",
        ownerVpa: "fitscan@upi",
        address: "123 Fitness Street, Health City, 560001",
        latitude: "12.9716",
        longitude: "77.5946",
        openHours: "06:00 AM - 10:00 PM",
        joiningdate: new Date().toISOString(),
        active: true
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchConsents = async () => {
    setLoadingConsents(true);
    try {
      const token = JSON.parse(localStorage.getItem('accessToken') || '{}').token;
      const response = await fetch(`${API_BASE_URL}/api/owner/milestone-consents`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setConsents(data);
      }
    } catch (err) {
      console.error('Failed to load consents:', err);
    } finally {
      setLoadingConsents(false);
    }
  };

  const handleToggleConsent = async (milestoneDays, currentOptIn) => {
    setUpdatingConsentId(milestoneDays);
    try {
      const token = JSON.parse(localStorage.getItem('accessToken') || '{}').token;
      const response = await fetch(`${API_BASE_URL}/api/owner/milestone-consents/${milestoneDays}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ hasOptedIn: !currentOptIn })
      });
      if (response.ok) {
        const updated = await response.json();
        setConsents(prev => {
          const exists = prev.some(c => c.milestoneDays === milestoneDays);
          if (exists) {
            return prev.map(c => c.milestoneDays === milestoneDays ? { ...c, hasOptedIn: updated.hasOptedIn } : c);
          } else {
            return [...prev, { milestoneDays, hasOptedIn: updated.hasOptedIn, updatedAt: updated.updatedAt }];
          }
        });
        showBanner('success', `Milestone agreement for ${milestoneDays} days updated successfully!`);
      }
    } catch (err) {
      console.error('Failed to toggle consent:', err);
      showBanner('error', 'Failed to update agreement setting.');
    } finally {
      setUpdatingConsentId(null);
    }
  };

  const renderAgreementsView = () => {
    const milestones = [
      {
        days: 30,
        title: "30-Day Milestone Reward",
        description: "Delivers FitScan Coins automatically. Full cost sponsored by FitScan. Recommended active.",
        costType: "Free (Sponsored by FitScan)",
        requiresConsent: false
      },
      {
        days: 60,
        title: "60-Day Milestone Reward",
        description: "Delivers a brand partner sponsor promo code. No cost to the gym. Recommended active.",
        costType: "Free (Brand Sponsor Discount)",
        requiresConsent: false
      },
      {
        days: 100,
        title: "100-Day Milestone (1 Month Free)",
        description: "Members completing 100 consistency scans receive 1 Month Free membership. Sponsoring this is at your gym's cost. If opted out, members receive a fallback sponsor discount instead.",
        costType: "Sponsored by Gym (At Gym Cost)",
        requiresConsent: true
      }
    ];

    return (
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => setActiveView(null)} className="p-2 bg-[#222] border border-[#2b2b2b] hover:bg-[#2b2b2b] rounded-lg text-gray-400 hover:text-white transition-all">
            <ArrowLeft size={18} />
          </button>
          <h2 className="text-2xl font-bold text-white">Milestone Agreements</h2>
        </div>

        {loadingConsents ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="animate-spin text-[#6CD800] mb-3" size={28} />
            <p className="text-gray-400 text-xs">Loading agreements...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {milestones.map((m) => {
              const consent = consents.find(c => c.milestoneDays === m.days);
              const hasOptedIn = consent ? consent.hasOptedIn : false;
              const isUpdating = updatingConsentId === m.days;

              return (
                <div key={m.days} className="bg-[#161616] border border-[#2b2b2b] rounded-2xl p-6 flex flex-col justify-between space-y-4">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <h4 className="text-base font-bold text-white">{m.title}</h4>
                      <p className="text-xs text-gray-400 mt-1 leading-relaxed">{m.description}</p>
                    </div>

                    <div className="shrink-0 pt-1">
                      {m.requiresConsent ? (
                        <label className="flex items-center cursor-pointer gap-2 select-none">
                          <div className="relative">
                            <input
                              type="checkbox"
                              checked={hasOptedIn}
                              disabled={isUpdating}
                              onChange={() => handleToggleConsent(m.days, hasOptedIn)}
                              className="sr-only"
                            />
                            <div className={`w-11 h-6 rounded-full transition-colors duration-200 ${hasOptedIn ? 'bg-[#6CD800]' : 'bg-[#2b2b2b]'}`}></div>
                            <div className={`absolute left-0.5 top-0.5 bg-[#161616] w-5 h-5 rounded-full transition-transform duration-200 ${hasOptedIn ? 'translate-x-5' : 'translate-x-0'}`}></div>
                          </div>
                        </label>
                      ) : (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                          Active (Default)
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="pt-3 border-t border-[#252525] flex justify-between items-center text-xs">
                    <span className="text-gray-500">Cost:</span>
                    <span className={`font-semibold ${m.requiresConsent ? 'text-yellow-400' : 'text-emerald-400'}`}>{m.costType}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const fetchFreezeSettings = async () => {
    try {
      setLoadingFreeze(true);
      const token = JSON.parse(localStorage.getItem('accessToken') || '{}').token;
      const response = await fetch(`${API_BASE_URL}/api/membershipfreeze/settings?gymCode=${userInfo?.gymcode || ''}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setFreezeSettings(data);
      }
    } catch (err) {
      console.error('Failed to load freeze settings:', err);
    } finally {
      setLoadingFreeze(false);
    }
  };

  const handleSaveFreezeSettings = async (acknowledged = false) => {
    setSavingFreeze(true);
    try {
      const token = JSON.parse(localStorage.getItem('accessToken') || '{}').token;
      const response = await fetch(`${API_BASE_URL}/api/membershipfreeze/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          gymCode: userInfo?.gymcode || '',
          isFreezeEnabled: freezeSettings.isFreezeEnabled,
          maxFreezeDaysPerYear: Number(freezeSettings.maxFreezeDaysPerYear),
          minFreezeDurationDays: Number(freezeSettings.minFreezeDurationDays),
          maxFreezeDurationDays: Number(freezeSettings.maxFreezeDurationDays),
          acknowledgedActiveFreezes: acknowledged
        })
      });

      if (response.status === 409) {
        const data = await response.json();
        setFreezeConflictCount(data.count);
        setShowDisableConfirm(true);
      } else if (response.ok) {
        showBanner('success', 'Freeze settings updated successfully!');
        setShowDisableConfirm(false);
      } else {
        const errText = await response.text();
        showBanner('error', errText || 'Failed to update freeze settings.');
      }
    } catch (err) {
      console.error('Failed to save freeze settings:', err);
      showBanner('error', 'An error occurred while saving.');
    } finally {
      setSavingFreeze(false);
    }
  };

  const renderFreezeSettingsView = () => {
    if (loadingFreeze) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="animate-spin text-[#6CD800] mb-3" size={28} />
          <p className="text-gray-400 text-xs">Loading freeze settings...</p>
        </div>
      );
    }

    return (
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => setActiveView(null)} className="p-2 bg-[#222] border border-[#2b2b2b] hover:bg-[#2b2b2b] rounded-lg text-gray-400 hover:text-white transition-all">
            <ArrowLeft size={18} />
          </button>
          <h2 className="text-2xl font-bold text-white">Membership Freeze Settings</h2>
        </div>

        <div className="bg-[#161616] border border-[#2b2b2b] rounded-2xl p-6 space-y-6">
          <div className="flex justify-between items-center pb-4 border-b border-[#252525]">
            <div>
              <h4 className="text-base font-bold text-white">Enable Membership Freeze</h4>
              <p className="text-xs text-gray-400 mt-1 leading-relaxed">Allow members to request temporary pauses on their membership.</p>
            </div>
            <label className="flex items-center cursor-pointer gap-2 select-none shrink-0">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={freezeSettings?.isFreezeEnabled || false}
                  onChange={(e) => setFreezeSettings(prev => ({ ...prev, isFreezeEnabled: e.target.checked }))}
                  className="sr-only"
                />
                <div className={`w-11 h-6 rounded-full transition-colors duration-200 ${freezeSettings?.isFreezeEnabled ? 'bg-[#6CD800]' : 'bg-[#2b2b2b]'}`}></div>
                <div className={`absolute left-0.5 top-0.5 bg-[#161616] w-5 h-5 rounded-full transition-transform duration-200 ${freezeSettings?.isFreezeEnabled ? 'translate-x-5' : 'translate-x-0'}`}></div>
              </div>
            </label>
          </div>

          {freezeSettings?.isFreezeEnabled && (
            <div className="space-y-4 animate-in fade-in duration-200">
              <div>
                <label className="block text-gray-400 text-xs uppercase tracking-widest font-bold mb-2 ml-1">Max Freeze Days Per Year</label>
                <input
                  type="number"
                  min="1"
                  value={freezeSettings.maxFreezeDaysPerYear}
                  onChange={(e) => setFreezeSettings(prev => ({ ...prev, maxFreezeDaysPerYear: Number(e.target.value) }))}
                  className="w-full px-4 py-3 bg-[#0d0d0d] border border-[#2b2b2b] rounded-xl text-white outline-none font-medium"
                />
                <p className="text-[10px] text-gray-500 mt-1">Maximum allowed frozen days reset yearly on membership anniversary date.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-xs uppercase tracking-widest font-bold mb-2 ml-1">Min Duration (Days)</label>
                  <input
                    type="number"
                    min="1"
                    value={freezeSettings.minFreezeDurationDays}
                    onChange={(e) => setFreezeSettings(prev => ({ ...prev, minFreezeDurationDays: Number(e.target.value) }))}
                    className="w-full px-4 py-3 bg-[#0d0d0d] border border-[#2b2b2b] rounded-xl text-white outline-none font-medium"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs uppercase tracking-widest font-bold mb-2 ml-1">Max Duration (Days)</label>
                  <input
                    type="number"
                    min="1"
                    value={freezeSettings.maxFreezeDurationDays}
                    onChange={(e) => setFreezeSettings(prev => ({ ...prev, maxFreezeDurationDays: Number(e.target.value) }))}
                    className="w-full px-4 py-3 bg-[#0d0d0d] border border-[#2b2b2b] rounded-xl text-white outline-none font-medium"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="pt-4 border-t border-[#252525]">
            <button
              onClick={() => handleSaveFreezeSettings(false)}
              disabled={savingFreeze || (freezeSettings?.isFreezeEnabled && freezeSettings?.minFreezeDurationDays > freezeSettings?.maxFreezeDurationDays)}
              className="w-full py-3 bg-[#6CD800] hover:bg-[#5bb300] disabled:opacity-50 text-zinc-950 font-bold rounded-xl text-sm transition-all"
            >
              {savingFreeze ? 'Saving...' : 'Save Settings'}
            </button>
            {freezeSettings?.isFreezeEnabled && freezeSettings?.minFreezeDurationDays > freezeSettings?.maxFreezeDurationDays && (
              <p className="text-red-500 text-[10px] mt-2 text-center">Min duration cannot exceed max duration.</p>
            )}
          </div>
        </div>

        {/* Confirmation Modal */}
        {showDisableConfirm && (
          <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowDisableConfirm(false)}>
            <div className="bg-[#181818] border border-[#2b2b2b] rounded-2xl max-w-sm w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <AlertCircle size={20} className="text-yellow-400 animate-pulse" />
                Active Freeze Requests Exist
              </h3>
              <p className="text-xs text-gray-400 leading-relaxed">
                You have {freezeConflictCount} active/pending/scheduled freeze requests. Disabling the freeze feature won't cancel these, but will block new requests. Do you want to continue?
              </p>
              <div className="flex gap-3 justify-end pt-2">
                <button
                  onClick={() => setShowDisableConfirm(false)}
                  className="px-4 py-2 rounded-lg bg-[#2a2a2a] hover:bg-[#3a3a3a] text-gray-300 text-xs border border-[#3b3b3b] font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSaveFreezeSettings(true)}
                  className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-xs font-medium"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#6CD800] mx-auto mb-4"></div>
          <p className="text-gray-400 text-sm font-medium">Loading settings...</p>
        </div>
      </div>
    );
  }

  const renderGymView = () => (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => setActiveView(null)} className="p-2 bg-[#222] border border-[#2b2b2b] hover:bg-[#2b2b2b] rounded-lg text-gray-400 hover:text-white transition-all">
          <ArrowLeft size={18} />
        </button>
        <h2 className="text-2xl font-bold text-white">Gym Details</h2>
      </div>

      <div className="bg-[#161616] border border-[#2b2b2b] rounded-2xl p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-400 text-xs uppercase tracking-widest font-bold mb-2 ml-1">Gym Name</label>
            <input value={gymData.name || ''} readOnly className="w-full px-4 py-3 bg-[#0d0d0d] border border-[#2b2b2b] rounded-xl text-white outline-none font-medium" />
          </div>
          <div>
            <label className="block text-gray-400 text-xs uppercase tracking-widest font-bold mb-2 ml-1">Phone Number</label>
            <input value={gymData.phonenumber || ''} readOnly className="w-full px-4 py-3 bg-[#0d0d0d] border border-[#2b2b2b] rounded-xl text-white outline-none font-medium" />
          </div>
          <div>
            <label className="block text-gray-400 text-xs uppercase tracking-widest font-bold mb-2 ml-1">Operating Hours</label>
            <input value={gymData.openHours || ''} readOnly className="w-full px-4 py-3 bg-[#0d0d0d] border border-[#2b2b2b] rounded-xl text-white outline-none font-medium" />
          </div>
          <div>
            <label className="block text-gray-400 text-xs uppercase tracking-widest font-bold mb-2 ml-1">Join Date</label>
            <input value={new Date(gymData.joiningdate).toLocaleDateString()} readOnly className="w-full px-4 py-3 bg-[#0d0d0d] border border-[#2b2b2b] rounded-xl text-white outline-none font-medium" />
          </div>
        </div>
        <div>
          <label className="block text-gray-400 text-xs uppercase tracking-widest font-bold mb-2 ml-1">Gym Address</label>
          <textarea value={gymData.address || ''} readOnly rows={3} className="w-full px-4 py-3 bg-[#0d0d0d] border border-[#2b2b2b] rounded-xl text-white outline-none font-medium resize-none" />
        </div>
        <div className="pt-4 border-t border-[#2b2b2b]">
          <p className="text-gray-500 text-sm">To update gym details, please contact support at <span className="text-[#6CD800]">support@fitscan.in</span></p>
        </div>
      </div>
    </div>
  );

  const renderOwnerView = () => (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => setActiveView(null)} className="p-2 bg-[#222] border border-[#2b2b2b] hover:bg-[#2b2b2b] rounded-lg text-gray-400 hover:text-white transition-all">
          <ArrowLeft size={18} />
        </button>
        <h2 className="text-2xl font-bold text-white">Owner Profile</h2>
      </div>

      <div className="bg-[#161616] border border-[#2b2b2b] rounded-2xl p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-400 text-xs uppercase tracking-widest font-bold mb-2 ml-1">Owner Name</label>
            <input value={gymData.ownerName || ''} readOnly className="w-full px-4 py-3 bg-[#0d0d0d] border border-[#2b2b2b] rounded-xl text-white outline-none font-medium" />
          </div>
          <div>
            <label className="block text-gray-400 text-xs uppercase tracking-widest font-bold mb-2 ml-1">Email Address</label>
            <input value={gymData.ownerEmail || ''} readOnly className="w-full px-4 py-3 bg-[#0d0d0d] border border-[#2b2b2b] rounded-xl text-white outline-none font-medium" />
          </div>
          <div>
            <label className="block text-gray-400 text-xs uppercase tracking-widest font-bold mb-2 ml-1">UPI ID (Payments)</label>
            <input value={gymData.ownerVpa || ''} readOnly className="w-full px-4 py-3 bg-[#0d0d0d] border border-[#2b2b2b] rounded-xl text-white outline-none font-medium" />
          </div>
          <div>
            <label className="block text-gray-400 text-xs uppercase tracking-widest font-bold mb-2 ml-1">Account Status</label>
            <div className="w-full px-4 py-3 bg-[#0d0d0d] border border-[#2b2b2b] rounded-xl">
              <span className={`inline-flex items-center gap-2 text-sm font-bold ${gymData.active ? 'text-[#6CD800]' : 'text-red-500'}`}>
                <div className={`w-2 h-2 rounded-full ${gymData.active ? 'bg-[#6CD800]' : 'bg-red-500'}`} />
                {gymData.active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAboutView = () => (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => setActiveView(null)} className="p-2 bg-[#222] border border-[#2b2b2b] hover:bg-[#2b2b2b] rounded-lg text-gray-400 hover:text-white transition-all">
          <ArrowLeft size={18} />
        </button>
        <h2 className="text-2xl font-bold text-white">About FitScan</h2>
      </div>

      <div className="bg-[#161616] border border-[#2b2b2b] rounded-2xl p-10 text-center shadow-xl">
        <div className="w-20 h-20 bg-[#6CD800] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#6CD800]/20">
          <svg width="40" height="40" viewBox="0 0 448 512"><path d="M0 80C0 53.5 21.5 32 48 32l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48L0 80zM64 96l0 64 64 0 0-64L64 96zM0 336c0-26.5 21.5-48 48-48l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48l0-96zm64 16l0 64 64 0 0-64-64 0zM304 32l96 0c26.5 0 48 21.5 48 48l0 96c0 26.5-21.5 48-48 48l-96 0c-26.5 0-48-21.5-48-48l0-96c0-26.5 21.5-48 48-48zm80 64l-64 0 0 64 64 0 0-64zM256 304c0-8.8 7.2-16 16-16l64 0c8.8 0 16 7.2 16 16s7.2 16 16 16l32 0c8.8 0 16-7.2 16-16s7.2-16 16-16s16 7.2 16 16l0 96c0 8.8-7.2 16-16 16l-64 0c-8.8 0-16-7.2-16-16s-7.2-16-16-16s-16 7.2-16 16l0 64c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-160zM368 480a16 16 0 1 1 0-32 16 16 0 1 1 0 32zm64 0a16 16 0 1 1 0-32 16 16 0 1 1 0 32z" fill="black" /></svg>
        </div>
        <h3 className="text-xl font-bold text-white mb-2">FitScan for Owners v1.0.0</h3>
        <p className="text-gray-400 leading-relaxed max-w-sm mx-auto mb-8">
          The ultimate control center for gym owners. Manage members, track attendance, and monitor revenue with ease.
        </p>
        <div className="pt-8 border-t border-[#2b2b2b] text-left space-y-4 max-w-sm mx-auto">
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-500">Official Support</span>
            <a href="mailto:support@fitscan.in" className="text-[#6CD800] font-medium">support@fitscan.in</a>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-500">Version Status</span>
            <span className="px-2 py-0.5 bg-[#6CD800]/10 text-[#6CD800] rounded-md text-[10px] font-bold tracking-widest uppercase">Stable</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-xl mx-auto py-8 px-4">
      {/* Banner message */}
      {banner.message && (
        <div className={`p-4 mb-4 rounded-xl border flex items-center gap-2 ${banner.type === 'success' ? 'bg-[#6CD800]/10 border-[#6CD800]/25 text-[#6CD800]' : 'bg-red-500/10 border-red-500/25 text-red-500'}`}>
          <Info size={16} />
          <p className="text-xs font-semibold">{banner.message}</p>
        </div>
      )}

      {activeView === 'gym' ? (
        renderGymView()
      ) : activeView === 'owner' ? (
        renderOwnerView()
      ) : activeView === 'agreements' ? (
        renderAgreementsView()
      ) : activeView === 'freeze' ? (
        renderFreezeSettingsView()
      ) : activeView === 'leaderboard' ? (
        <LeaderboardView gymId={gymData?.code} isAdmin={false} onBack={() => setActiveView(null)} />
      ) : activeView === 'help' ? (
        <HelpScreen onBack={() => setActiveView(null)} gymCode={gymData?.code} />
      ) : activeView === 'about' ? (
        renderAboutView()
      ) : (
        <div className="space-y-8">
          {/* Minimalist Header */}
          <div className="flex flex-col items-center gap-4 text-white pb-6">
            <div className="w-24 h-24 rounded-3xl bg-[#6CD800] flex items-center justify-center shadow-lg shadow-[#6CD800]/20 rotate-3">
              <Shield size={48} className="text-black -rotate-3" />
            </div>
            <div className="text-center">
              <h1 className="text-2xl font-black tracking-tight">{gymData.name || "FitScan Gym"}</h1>
              <p className="text-gray-500 text-sm font-medium mt-1 uppercase tracking-widest">Premium Gym Account</p>
            </div>
          </div>

          {/* Menu Items */}
          <div className="space-y-3">
            {[
              { id: 'gym', icon: <Building2 size={20} />, label: "Gym Details", color: "text-blue-400" },
              { id: 'owner', icon: <UserCircle size={20} />, label: "Owner Profile", color: "text-[#6CD800]" },
              { id: 'agreements', icon: <Award size={20} />, label: "Milestone Agreements", color: "text-purple-400" },
              { id: 'freeze', icon: <Clock size={20} />, label: "Membership Freeze", color: "text-red-400" },
              { id: 'leaderboard', icon: <Award size={20} />, label: "Leaderboard", color: "text-yellow-400" },
              { id: 'help', icon: <HelpCircle size={20} />, label: "Help & FAQ", color: "text-amber-400" },
              { id: 'about', icon: <Info size={20} />, label: "About FitScan", color: "text-gray-400" },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveView(item.id)}
                className="flex items-center justify-between w-full px-5 py-4 bg-[#161616] hover:bg-[#202020] border border-[#2b2b2b] rounded-2xl text-gray-200 transition-all duration-200 active:scale-[0.98] group"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl bg-[#222] flex items-center justify-center ${item.color} group-hover:bg-[#6CD800] group-hover:text-black transition-colors duration-300`}>
                    {item.icon}
                  </div>
                  <span className="text-left font-bold text-[15px] tracking-wide">{item.label}</span>
                </div>
                <ChevronRight size={18} className="text-gray-600 group-hover:text-[#6CD800] transition-colors" />
              </button>
            ))}
          </div>

          {/* Logout Section */}
          <div className="pt-8">
            <button
              onClick={async () => { await logout(); window.location.href = '/'; }}
              className="w-full py-4 rounded-2xl border border-red-500/30 text-red-500 font-bold hover:bg-red-500 hover:text-white transition-all duration-300 active:scale-[0.98] flex items-center justify-center gap-2"
            >
              <LogOut size={18} />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsTab;
